import {useCurrencyConfig} from './I18nContext';

type NumericalDelimiter = ',' | '.';

export interface Currency {
  decimalPoint: NumericalDelimiter;
  iso: string;
  negativeSign: string;
  sign: Record<string, string>;
  signPosition: 'before' | 'after';
  truncate: boolean;
  thousandsSeparator: NumericalDelimiter | ' ' | "'" | '';
  useIsoSign: boolean;
  hideVatInfo?: boolean;
}

const addSign = (
  price: string,
  signPosition: 'before' | 'after',
  sign: string,
  withoutCurrencySymbol?: boolean,
) => {
  if (withoutCurrencySymbol) return price;
  return signPosition === 'after' ? `${price} ${sign}` : `${sign} ${price}`;
};

const addThousandSeparator = (
  preDecimal: number,
  separator: NumericalDelimiter | ' ' | "'" | '',
): string => {
  const chars = String(preDecimal).split('');
  chars.splice(chars.length - 3, 0, separator);
  return chars.join('');
};

const formatPrice = (
  price: number,
  currencyCode: string,
  currency: Currency,
  options?: {
    withoutDecimal?: boolean;
    useIsoSign?: boolean;
    withoutCurrencySymbol?: boolean;
    truncateOverride?: boolean;
    overrideDefaultLimiter?: string;
    overrideThousandsSeparator?: Currency['thousandsSeparator'];
  },
): string => {
  const {
    decimalPoint: defaultDecimalPoint,
    negativeSign,
    sign,
    signPosition,
    thousandsSeparator: defaultThousandsSeparator,
    truncate,
    useIsoSign,
  } = currency;

  if (isNaN(price)) {
    return '';
  }
  const decimalPoint = options?.overrideDefaultLimiter
    ? options?.overrideDefaultLimiter
    : defaultDecimalPoint;

  const thousandsSeparator =
    options?.overrideThousandsSeparator ?? defaultThousandsSeparator;

  const negativeStr = price < 0 ? negativeSign : '';
  const round = price < 0 ? Math.ceil : Math.floor;
  const preDecimal =
    options && options.withoutDecimal
      ? Math.abs(Math.round(price / 100))
      : Math.abs(round(price / 100));
  const preDecimalStr =
    preDecimal > 999
      ? addThousandSeparator(preDecimal, thousandsSeparator)
      : preDecimal;

  let afterTruncateCents = '';
  if (!(options && options.withoutDecimal)) {
    const rest = Math.abs(price % 100);
    const cents = rest < 10 ? `0${rest}` : rest;
    afterTruncateCents = truncate ? '' : `${decimalPoint}${cents}`;
    afterTruncateCents =
      options && options.truncateOverride
        ? `${decimalPoint}${cents}`
        : afterTruncateCents;
  }

  const priceStr = `${negativeStr}${preDecimalStr}${afterTruncateCents}`;

  const isoSign = options?.useIsoSign ?? useIsoSign;

  return addSign(
    priceStr,
    signPosition,
    isoSign ? currencyCode : sign[currencyCode] ?? currencyCode,
    options && options.withoutCurrencySymbol,
  );
};

type FormatCurrencyOptions = Parameters<typeof formatPrice>[3];

export const formatCurrency =
  (currencyConfig: Currency) =>
  (
    price: number,
    currencyCode: string,
    options?: FormatCurrencyOptions,
  ): string => {
    return formatPrice(price, currencyCode, currencyConfig, options);
  };

export const useFormatCurrency = () => {
  const currencyConfig = useCurrencyConfig();

  return formatCurrency(currencyConfig);
};
